// FP3-035 — Assessment Runner MVP.
//
// Provider-administered informal probes. The provider picks a type
// (Skill Probe / Rubric-Rating / Timed Trial), adds items, scores
// them, and saves either a draft or completed administration. The
// completed record becomes structured evidence the Goal Plan AI
// can read alongside uploaded documents.
//
// Important boundary: nothing in this component embeds copyrighted
// standardized test items or norm-referenced scoring formulas. The
// provider authors the items; we just store + roll up the scores.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ClipboardText,
  Plus,
  TrashSimple,
  Check,
  XCircle,
  CaretLeft,
  Star,
  Timer,
  PlayCircle,
  PauseCircle,
  ArrowsCounterClockwise,
  Smiley,
} from "@phosphor-icons/react";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import ArticulationScreenerStep from "@/components/student/ArticulationScreenerStep";
import ArticulationScreenerPreflightModal, {
  articulationPreflightMissing,
} from "@/components/student/ArticulationScreenerPreflightModal";

const TYPE_META = {
  skill_probe: {
    label: "Skill Probe",
    short: "Correct / incorrect items",
    description:
      "Score each item correct or incorrect. App calculates percent accuracy.",
    icon: ClipboardText,
  },
  rubric_rating: {
    label: "Rubric / Rating Probe",
    short: "1–5 rating per criterion",
    description:
      "Rate each criterion 1–5. App calculates the average rating.",
    icon: Star,
  },
  timed_trial: {
    label: "Timed Trial Probe",
    short: "Trials with optional timer",
    description:
      "Record correct/incorrect trials during a timed activity. App calculates accuracy and trials/minute.",
    icon: Timer,
  },
  articulation_screener: {
    label: "Articulation Screener",
    short: "Picture-prompt sound screen (SLP/SLPA)",
    description:
      "Step through provider-authored screener items. Student Viewer shows the picture prompt on a separate tab/device.",
    icon: Smiley,
  },
};

const DEFAULT_TIMER_PRESETS = [60, 90, 120, 180, 300];

const blankItem = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 9)}`,
  prompt: "",
  score: null,
  notes: "",
});

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function AssessmentRunnerDialog({
  open,
  onOpenChange,
  student,
  existingRun,           // optional — pass a draft run to resume editing
  onSaved,
}) {
  const [step, setStep] = useState("type"); // type → run
  const [assessmentType, setAssessmentType] = useState("skill_probe");
  const [title, setTitle] = useState("");
  const [skillArea, setSkillArea] = useState("");
  const [dateAdministered, setDateAdministered] = useState(todayIso());
  const [items, setItems] = useState([blankItem(), blankItem(), blankItem()]);
  const [providerNotes, setProviderNotes] = useState("");
  const [clinicalSummary, setClinicalSummary] = useState("");
  const [recommendations, setRecommendations] = useState("");

  // Timed-trial timer state (untimed = null seconds).
  const [timerSeconds, setTimerSeconds] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerElapsed, setTimerElapsed] = useState(0);
  const tickRef = useRef(null);

  // Run-id tracking — when set, we PATCH instead of POSTing.
  const [runId, setRunId] = useState(null);
  const [saving, setSaving] = useState(false);

  // FP3-080B — Articulation Screener preflight gate. Blocks the
  // "Continue" transition when the selected student is missing
  // DOB or a norm-eligible Sex value. Live student doc after the
  // preflight PATCH lands in liveStudent so the screener step
  // (which reads student.sex/DOB through props) sees the fresh
  // demographic data without a page refresh.
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [liveStudent, setLiveStudent] = useState(student);
  useEffect(() => {
    setLiveStudent(student);
  }, [student]);

  // Hydrate from existing draft on open.
  useEffect(() => {
    if (!open) return;
    if (existingRun) {
      setAssessmentType(existingRun.assessment_type);
      setTitle(existingRun.title || "");
      setSkillArea(existingRun.skill_area || "");
      setDateAdministered(existingRun.date_administered || todayIso());
      setItems(
        (existingRun.items && existingRun.items.length
          ? existingRun.items.map((i) => ({ ...i }))
          : [blankItem()]),
      );
      setProviderNotes(existingRun.provider_notes || "");
      setClinicalSummary(existingRun.clinical_summary || "");
      setRecommendations(existingRun.recommendations || "");
      setTimerSeconds(existingRun.timer_seconds || null);
      setRunId(existingRun.id);
      setStep("run");
    } else {
      setStep("type");
      setAssessmentType("skill_probe");
      setTitle("");
      setSkillArea("");
      setDateAdministered(todayIso());
      setItems([blankItem(), blankItem(), blankItem()]);
      setProviderNotes("");
      setClinicalSummary("");
      setRecommendations("");
      setTimerSeconds(null);
      setRunId(null);
    }
    setTimerRunning(false);
    setTimerElapsed(0);
  }, [open, existingRun]);

  // Tick timer.
  useEffect(() => {
    if (!timerRunning) {
      clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      setTimerElapsed((e) => e + 1);
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [timerRunning]);

  const summary = useMemo(() => {
    if (assessmentType === "rubric_rating") {
      const scored = items.filter((i) => typeof i.score === "number");
      const avg =
        scored.length === 0
          ? null
          : Math.round((scored.reduce((s, i) => s + i.score, 0) / scored.length) * 100) / 100;
      return {
        total_count: items.length,
        scored_count: scored.length,
        average_rating: avg,
      };
    }
    const scored = items.filter((i) => i.score === 0 || i.score === 1);
    const correct = scored.filter((i) => i.score === 1).length;
    const accuracy = scored.length
      ? Math.round((correct / scored.length) * 1000) / 10
      : null;
    let tpm = null;
    if (assessmentType === "timed_trial" && timerSeconds && scored.length) {
      tpm = Math.round((scored.length / (timerSeconds / 60)) * 100) / 100;
    }
    return {
      total_count: items.length,
      scored_count: scored.length,
      correct_count: correct,
      incorrect_count: scored.length - correct,
      percent_accuracy: accuracy,
      trials_per_minute: tpm,
    };
  }, [items, assessmentType, timerSeconds]);

  const isRubric = assessmentType === "rubric_rating";
  const isTimed = assessmentType === "timed_trial";

  const updateItem = (idx, patch) =>
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) =>
    setItems((cur) => cur.filter((_, i) => i !== idx));
  const addItem = () => setItems((cur) => [...cur, blankItem()]);
  // Timed-trial — providers tap "Correct" / "Incorrect" buttons to
  // append a new pre-scored trial row, which keeps the rapid-fire
  // documentation in line with how a real probe is run.
  const appendTrial = (correct) =>
    setItems((cur) => [
      ...cur,
      {
        id: `tmp-${Math.random().toString(36).slice(2, 9)}`,
        prompt: `Trial ${cur.length + 1}`,
        score: correct ? 1 : 0,
        notes: "",
      },
    ]);

  // Serialize the item list for the API. The server normalizes
  // ids and clamps scores so tmp- prefixes are fine.
  const serializeItems = () =>
    items.map((it) => ({
      id: it.id?.startsWith("tmp-") ? null : it.id,
      prompt: (it.prompt || "").trim(),
      score: typeof it.score === "number" ? it.score : null,
      notes: (it.notes || "").trim(),
    }));

  const buildPayload = () => ({
    assessment_type: assessmentType,
    title,
    skill_area: skillArea,
    date_administered: dateAdministered,
    timer_seconds: isTimed && timerSeconds ? timerSeconds : null,
    items: serializeItems(),
    provider_notes: providerNotes,
    clinical_summary: clinicalSummary,
    recommendations,
  });

  const persistDraft = async () => {
    if (!title.trim()) {
      toast.error("Please enter an assessment title.");
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (runId) {
        const r = await api.patchAssessmentRun(student.id, runId, payload);
        return r.data;
      }
      const r = await api.createAssessmentRun(student.id, payload);
      setRunId(r.data.id);
      return r.data;
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Failed to save draft";
      toast.error(msg);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    const saved = await persistDraft();
    if (saved) {
      toast.success("Draft saved");
      onSaved?.(saved);
    }
  };

  const handleComplete = async () => {
    const saved = await persistDraft();
    if (!saved) return;
    setSaving(true);
    try {
      const r = await api.completeAssessmentRun(student.id, saved.id);
      toast.success("Assessment completed");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Failed to complete assessment";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Renderers ──────────────────────────────────────────────────────

  const renderTypeStep = () => (
    <div className="flex flex-col gap-3 px-6 py-4">
      <p className="text-xs text-[#6B7280]">
        Pick a probe style. Items, scoring, and the result summary
        adapt to the type you choose.
      </p>
      {Object.entries(TYPE_META).map(([key, meta]) => {
        const Icon = meta.icon;
        const active = assessmentType === key;
        return (
          <button
            key={key}
            data-testid={`assessment-runner-type-${key}`}
            onClick={() => setAssessmentType(key)}
            className={`flex items-start gap-3 text-left rounded-xl border p-3 transition-colors ${
              active
                ? "border-[#1E3F33] bg-[#F1F7F2]"
                : "border-[#E5E3DB] bg-white hover:border-[#9B9890]"
            }`}
          >
            <span
              className={`inline-flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${
                active
                  ? "bg-[#1E3F33] text-white"
                  : "bg-white text-[#1E3F33] border border-[#E5E3DB]"
              }`}
            >
              <Icon size={20} weight="duotone" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold text-[#1F2937]">
                {meta.label}
              </span>
              <span className="block text-[11px] text-[#6B7280] mt-0.5">
                {meta.short}
              </span>
              <span className="block text-[11px] text-[#4B5563] mt-1.5 leading-snug">
                {meta.description}
              </span>
            </span>
            {active && (
              <Check size={16} weight="bold" className="text-[#1E3F33] mt-2" />
            )}
          </button>
        );
      })}
    </div>
  );

  const renderRunStep = () => {
    const Icon = TYPE_META[assessmentType].icon;
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
        {/* Header strip */}
        <div className="flex flex-wrap items-end gap-3">
          <span
            data-testid="assessment-runner-active-type"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-[#1E3F33] bg-[#E8F0EB] px-2 py-1 rounded-full"
          >
            <Icon size={12} weight="fill" /> {TYPE_META[assessmentType].label}
          </span>
          <button
            type="button"
            data-testid="assessment-runner-change-type"
            onClick={() => setStep("type")}
            className="text-[11px] text-[#6B7280] hover:text-[#1E3F33] inline-flex items-center gap-1"
          >
            <CaretLeft size={11} /> change type
          </button>
        </div>

        {/* Title / Skill / Date row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-6">
            <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              Title
            </Label>
            <Input
              data-testid="assessment-runner-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`e.g. ${
                assessmentType === "skill_probe"
                  ? "Articulation /s/ initial probe"
                  : assessmentType === "rubric_rating"
                  ? "Narrative writing rubric — May"
                  : "Math facts fluency check"
              }`}
              className="text-xs h-9"
            />
          </div>
          <div className="md:col-span-4">
            <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              Skill area / domain
            </Label>
            <Input
              data-testid="assessment-runner-skill-area"
              value={skillArea}
              onChange={(e) => setSkillArea(e.target.value)}
              placeholder="e.g. Speech — Articulation"
              className="text-xs h-9"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              Date
            </Label>
            <Input
              data-testid="assessment-runner-date"
              type="date"
              value={dateAdministered}
              onChange={(e) => setDateAdministered(e.target.value)}
              className="text-xs h-9"
            />
          </div>
        </div>

        {/* Timer (timed_trial only) */}
        {isTimed && (
          <div className="rounded-xl border border-[#E5E3DB] bg-white p-3 space-y-3" data-testid="assessment-runner-timer">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-[11px] uppercase tracking-wide font-semibold text-[#4B5563]">
                Timer
              </Label>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-[#6B7280] mr-1">Duration</span>
                {DEFAULT_TIMER_PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`assessment-runner-timer-${p}`}
                    onClick={() => setTimerSeconds(p)}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                      timerSeconds === p
                        ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                        : "bg-white text-[#6B7280] border-[#E5E3DB]"
                    }`}
                  >
                    {p < 60 ? `${p}s` : `${p / 60}m`}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="assessment-runner-timer-untimed"
                  onClick={() => setTimerSeconds(null)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                    timerSeconds == null
                      ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                      : "bg-white text-[#6B7280] border-[#E5E3DB]"
                  }`}
                >
                  Untimed
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                data-testid="assessment-runner-timer-display"
                className="font-mono text-2xl text-[#1F2937] tabular-nums"
              >
                {Math.floor(timerElapsed / 60).toString().padStart(2, "0")}:
                {(timerElapsed % 60).toString().padStart(2, "0")}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid={`assessment-runner-timer-${timerRunning ? "pause" : "start"}`}
                onClick={() => setTimerRunning((r) => !r)}
                className="h-8 text-xs"
              >
                {timerRunning ? <PauseCircle size={14} className="mr-1" /> : <PlayCircle size={14} className="mr-1" />}
                {timerRunning ? "Pause" : "Start"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-testid="assessment-runner-timer-reset"
                onClick={() => {
                  setTimerRunning(false);
                  setTimerElapsed(0);
                }}
                className="h-8 text-xs"
              >
                <ArrowsCounterClockwise size={14} className="mr-1" /> Reset
              </Button>
              {timerSeconds && (
                <span className="text-[11px] text-[#6B7280]">
                  Target: {Math.floor(timerSeconds / 60)}:
                  {(timerSeconds % 60).toString().padStart(2, "0")}
                </span>
              )}
            </div>
            {/* Rapid-fire trial appenders */}
            <div className="flex items-center gap-2 pt-1 border-t border-[#F1F2F4]">
              <Button
                type="button"
                size="sm"
                data-testid="assessment-runner-trial-correct"
                onClick={() => appendTrial(true)}
                className="h-8 text-xs bg-[#E8F0EB] text-[#1E3F33] hover:bg-[#D5E5D9] border border-[#1E3F33]/30"
              >
                <Check size={13} className="mr-1" /> Correct trial
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="assessment-runner-trial-incorrect"
                onClick={() => appendTrial(false)}
                className="h-8 text-xs"
              >
                <XCircle size={13} className="mr-1" /> Incorrect trial
              </Button>
            </div>
          </div>
        )}

        {/* Items / criteria */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] uppercase tracking-wide font-semibold text-[#4B5563]">
              {isRubric ? "Criteria" : "Items"} ({items.length})
            </Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="assessment-runner-add-item"
              onClick={addItem}
              className="h-8 text-xs"
            >
              <Plus size={13} className="mr-1" /> Add {isRubric ? "criterion" : "item"}
            </Button>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <ItemRow
                key={it.id || idx}
                idx={idx}
                item={it}
                isRubric={isRubric}
                onUpdate={(patch) => updateItem(idx, patch)}
                onRemove={() => removeItem(idx)}
              />
            ))}
            {items.length === 0 && (
              <p className="text-[11px] text-[#6B7280] italic">
                No items yet — add at least one before completing.
              </p>
            )}
          </div>
        </div>

        {/* Live summary */}
        <div
          data-testid="assessment-runner-summary"
          className="rounded-xl border border-[#E5E3DB] bg-[#F9F8F6] p-3 grid grid-cols-2 md:grid-cols-4 gap-3"
        >
          <SummaryStat label="Items" value={summary.total_count} />
          <SummaryStat label="Scored" value={summary.scored_count} />
          {isRubric ? (
            <SummaryStat
              label="Avg rating"
              value={
                summary.average_rating == null
                  ? "—"
                  : `${summary.average_rating}/5`
              }
              accent
            />
          ) : (
            <>
              <SummaryStat
                label="Correct / Total"
                value={
                  summary.scored_count
                    ? `${summary.correct_count}/${summary.scored_count}`
                    : "—"
                }
              />
              <SummaryStat
                label="Accuracy"
                value={
                  summary.percent_accuracy == null
                    ? "—"
                    : `${summary.percent_accuracy}%`
                }
                accent
              />
              {isTimed && (
                <SummaryStat
                  label="Trials / min"
                  value={summary.trials_per_minute ?? "—"}
                />
              )}
            </>
          )}
        </div>

        {/* Notes / clinical summary / recommendations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              Provider notes
            </Label>
            <Textarea
              data-testid="assessment-runner-provider-notes"
              value={providerNotes}
              onChange={(e) => setProviderNotes(e.target.value)}
              placeholder="Observations during administration — student behavior, prompts given, etc."
              className="text-xs min-h-[72px]"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              Clinical / educational summary
            </Label>
            <Textarea
              data-testid="assessment-runner-clinical-summary"
              value={clinicalSummary}
              onChange={(e) => setClinicalSummary(e.target.value)}
              placeholder="What did this probe tell you about the student's current skills?"
              className="text-xs min-h-[72px]"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              Recommendations / next steps
            </Label>
            <Textarea
              data-testid="assessment-runner-recommendations"
              value={recommendations}
              onChange={(e) => setRecommendations(e.target.value)}
              placeholder="Suggested follow-up — target updates, additional probes, instructional shifts."
              className="text-xs min-h-[60px]"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="assessment-runner-dialog"
        aria-describedby={undefined}
        className="w-[95vw] sm:w-[90vw] sm:max-w-[1080px] max-h-[90vh] p-0 bg-[#FBFAF6] flex flex-col gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#ECEAE4] shrink-0">
          <DialogTitle className="text-[#1F2937] heading-font flex items-center gap-2">
            <ClipboardText size={18} weight="duotone" className="text-[#9C6B2E]" />
            {existingRun ? "Resume assessment" : "Start assessment"}
            {student && (
              <span className="ml-2 text-xs text-[#6B7280] font-normal">
                · {student.first_name} {student.last_name}
              </span>
            )}
          </DialogTitle>
          <p className="text-xs text-[#6B7280] mt-1">
            {step === "type"
              ? "Pick a probe style to begin."
              : "Add items, score, and save as a draft or completed administration."}
          </p>
        </DialogHeader>

        {step === "type" ? (
          <div className="flex-1 min-h-0 overflow-y-auto">{renderTypeStep()}</div>
        ) : assessmentType === "articulation_screener" ? (
          <ArticulationScreenerStep
            student={liveStudent}
            existingRun={existingRun}
            onCompleted={(saved) => {
              onSaved?.(saved);
              onOpenChange(false);
            }}
            onCancel={() => onOpenChange(false)}
          />
        ) : (
          renderRunStep()
        )}

        {/* Footer is hidden for the screener step — it owns its own
            action buttons since the flow is item-by-item rather than
            a single-page form. */}
        {!(step === "run" && assessmentType === "articulation_screener") && (
          <DialogFooter className="px-6 py-3 border-t border-[#ECEAE4] bg-[#F6F4EE] shrink-0">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="assessment-runner-cancel"
            >
              Cancel
            </Button>
            {step === "type" ? (
              <Button
                data-testid="assessment-runner-next"
                onClick={() => {
                  // FP3-080B — Articulation Screener requires valid DOB
                  // + Sex before starting. Other probe types skip this
                  // gate entirely.
                  if (assessmentType === "articulation_screener") {
                    const check = articulationPreflightMissing(liveStudent);
                    if (!check.ready) {
                      setPreflightOpen(true);
                      return;
                    }
                  }
                  setStep("run");
                }}
                className="bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                Continue
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  data-testid="assessment-runner-save-draft"
                  disabled={saving || !title.trim()}
                  onClick={handleSaveDraft}
                >
                  Save draft
                </Button>
                <Button
                  data-testid="assessment-runner-complete"
                  disabled={saving || !title.trim() || summary.scored_count === 0}
                  onClick={handleComplete}
                  className="bg-[#1E3F33] hover:bg-[#173127] text-white"
                >
                  {saving ? "Saving…" : "Complete assessment"}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
    <ArticulationScreenerPreflightModal
      open={preflightOpen}
      onOpenChange={setPreflightOpen}
      student={liveStudent}
      onReady={(updatedStudent) => {
        // Refresh the local student cache so downstream screener
        // props read the freshly PATCHed DOB/Sex — no page reload.
        setLiveStudent(updatedStudent);
        setPreflightOpen(false);
        setStep("run");
      }}
    />
    </>
  );
}

function ItemRow({ idx, item, isRubric, onUpdate, onRemove }) {
  return (
    <div
      data-testid={`assessment-runner-item-${idx}`}
      className="grid grid-cols-12 gap-2 items-start rounded-lg border border-[#E5E3DB] bg-white p-2"
    >
      <div className="col-span-12 md:col-span-6">
        <Label className="text-[9px] uppercase tracking-wide text-[#6B7280]">
          {isRubric ? `Criterion ${idx + 1}` : `Item ${idx + 1}`}
        </Label>
        <Input
          data-testid={`assessment-runner-item-prompt-${idx}`}
          value={item.prompt}
          onChange={(e) => onUpdate({ prompt: e.target.value })}
          placeholder={
            isRubric
              ? "Criterion description (e.g. Topic stays on focus)"
              : "Item prompt (e.g. word 'snake')"
          }
          className="text-xs h-8"
        />
      </div>
      <div className="col-span-8 md:col-span-4">
        <Label className="text-[9px] uppercase tracking-wide text-[#6B7280]">
          Score
        </Label>
        {isRubric ? (
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`assessment-runner-item-rubric-${idx}-${n}`}
                onClick={() => onUpdate({ score: n })}
                className={`flex-1 h-8 rounded border text-xs font-medium transition ${
                  item.score === n
                    ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                    : "bg-white text-[#6B7280] border-[#E5E3DB] hover:border-[#9B9890]"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={`assessment-runner-item-correct-${idx}`}
              onClick={() => onUpdate({ score: 1 })}
              className={`flex-1 h-8 rounded border text-xs font-semibold transition inline-flex items-center justify-center gap-1 ${
                item.score === 1
                  ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                  : "bg-white text-[#1E3F33] border-[#1E3F33]/30 hover:bg-[#E8F0EB]"
              }`}
            >
              <Check size={12} /> Correct
            </button>
            <button
              type="button"
              data-testid={`assessment-runner-item-incorrect-${idx}`}
              onClick={() => onUpdate({ score: 0 })}
              className={`flex-1 h-8 rounded border text-xs font-semibold transition inline-flex items-center justify-center gap-1 ${
                item.score === 0
                  ? "bg-[#7A3A2C] text-white border-[#7A3A2C]"
                  : "bg-white text-[#7A3A2C] border-[#7A3A2C]/30 hover:bg-[#F8E8E2]"
              }`}
            >
              <XCircle size={12} /> Incorrect
            </button>
            {item.score !== null && (
              <button
                type="button"
                data-testid={`assessment-runner-item-clear-${idx}`}
                onClick={() => onUpdate({ score: null })}
                className="h-8 px-1.5 rounded border border-[#E5E3DB] text-[10px] text-[#6B7280] hover:bg-[#F3F1EC]"
                title="Clear score"
              >
                clear
              </button>
            )}
          </div>
        )}
      </div>
      <div className="col-span-4 md:col-span-2 text-right">
        <Label className="text-[9px] uppercase tracking-wide text-[#6B7280] invisible md:visible">
          &nbsp;
        </Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid={`assessment-runner-item-remove-${idx}`}
          onClick={onRemove}
          className="h-8 text-xs text-[#7A3A2C] hover:bg-[#F8E8E2] w-full"
        >
          <TrashSimple size={13} />
        </Button>
      </div>
      {item.notes != null && (
        <div className="col-span-12">
          <Input
            data-testid={`assessment-runner-item-notes-${idx}`}
            value={item.notes || ""}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Notes (optional)"
            className="text-[11px] h-7 border-[#F1F2F4]"
          />
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, accent = false }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">
        {label}
      </p>
      <p
        className={`text-base font-semibold tabular-nums ${
          accent ? "text-[#1E3F33]" : "text-[#1F2937]"
        }`}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}
